<template>
  <section class="premium-delivery-admin">
    <div class="premium-delivery-admin__status" :class="{ 'premium-delivery-admin__status--complete': delivery }">
      <v-icon size="22" :color="deliveryLoadError ? '#b42318' : delivery ? '#ae7a22' : '#8a817b'">
        {{ deliveryLoadError ? "mdi-alert-circle-outline" : delivery ? "mdi-check-circle" : "mdi-clock-outline" }}
      </v-icon>
      <strong v-if="loadingDelivery">기존 전달 상태 확인 중</strong>
      <strong v-else-if="deliveryLoadError">기존 전달 상태를 확인하지 못했습니다</strong>
      <strong v-else-if="delivery">전달 완료</strong>
      <strong v-else>전달 전</strong>
    </div>

    <section v-if="uploading || uploadFailed" class="premium-delivery-admin__progress" aria-live="polite">
      <div class="premium-delivery-admin__progress-copy">
        <strong>{{ uploading ? `고객에게 전달 중 ${uploadPercent}%` : "전달이 중단되었습니다" }}</strong>
        <span v-if="uploading">{{ formatFileSize(uploadLoadedBytes) }} / {{ formatFileSize(uploadTotalBytes || selectedTotalBytes) }}</span>
        <span v-else>선택한 파일은 그대로 남아 있습니다. 다시 시도할 수 있습니다.</span>
      </div>
      <v-progress-linear
        :value="uploadPercent"
        :color="uploadFailed ? '#b42318' : '#7d1832'"
        height="8"
        rounded
      />
      <v-btn v-if="uploading" text small color="#7d1832" class="premium-delivery-admin__cancel" @click="cancelUpload">전송 취소</v-btn>
      <v-btn
        v-else-if="uploadFailed"
        text
        small
        color="#7d1832"
        class="premium-delivery-admin__cancel"
        :disabled="!canPublish"
        @click="publish"
      >
        다시 시도
      </v-btn>
    </section>

    <section
      class="premium-delivery-admin__shelf premium-delivery-admin__shelf--completed"
      :class="{ 'premium-delivery-admin__shelf--active': draggingCompleted }"
      @dragover.prevent="draggingCompleted = true"
      @dragleave.prevent="draggingCompleted = false"
      @drop.prevent.stop="onDropCompleted"
    >
      <div class="premium-delivery-admin__shelf-heading">
        <strong>완성 사진 <small>{{ deliveredCompleted.length }}장</small></strong>
        <v-btn
          outlined
          small
          color="#7d1832"
          class="premium-delivery-admin__add"
          :disabled="uploading"
          @click.stop="triggerCompletedInput"
        >
          <v-icon left size="17">mdi-plus</v-icon>
          완성 사진 추가
        </v-btn>
      </div>
      <input
        ref="completedInput"
        class="premium-delivery-admin__input"
        type="file"
        multiple
        accept=".jpg,.jpeg,image/jpeg"
        @change="onCompletedInput"
        :disabled="uploading"
      />
      <ul v-if="deliveredCompleted.length" class="premium-delivery-admin__completed-list">
        <li v-for="asset in deliveredCompleted" :key="asset.id" :title="asset.name">
          <img :src="asset.thumbnail_url || asset.url" :alt="asset.name || '전달된 완성 사진'" />
          <v-btn
            icon
            x-small
            color="#9d2d36"
            class="premium-delivery-admin__delete"
            :loading="deletingAssetId === asset.id"
            :disabled="uploading || Boolean(deletingAssetId)"
            :aria-label="`${asset.name || '완성 사진'} 삭제`"
            @click="deletePublished(asset)"
          >
            <v-icon size="18">mdi-delete-outline</v-icon>
          </v-btn>
        </li>
      </ul>
      <button
        v-else
        type="button"
        class="premium-delivery-admin__empty"
        :disabled="uploading"
        @click="triggerCompletedInput"
      >
        완성 사진을 추가해 주세요
      </button>
    </section>

    <section
      class="premium-delivery-admin__shelf premium-delivery-admin__shelf--originals"
      :class="{ 'premium-delivery-admin__shelf--active': draggingOriginals }"
      @dragover.prevent="draggingOriginals = true"
      @dragleave.prevent="draggingOriginals = false"
      @drop.prevent.stop="onDropOriginals"
    >
      <div class="premium-delivery-admin__shelf-heading">
        <strong>원본 사진 <small>{{ deliveredOriginalCount }}장</small></strong>
        <div class="premium-delivery-admin__heading-actions">
          <v-btn
            v-if="deliveredOriginals.length"
            text
            small
            color="#9d2d36"
            class="premium-delivery-admin__delete-all"
            :loading="deletingOriginals"
            :disabled="uploading || Boolean(deletingAssetId)"
            @click.stop="deleteAllOriginals"
          >
            전체 삭제
          </v-btn>
          <v-btn
            outlined
            small
            color="#7d1832"
            class="premium-delivery-admin__add"
            :disabled="uploading || deletingOriginals || Boolean(deletingAssetId)"
            @click.stop="triggerOriginalInput"
          >
            <v-icon left size="17">mdi-plus</v-icon>
            원본 사진 추가
          </v-btn>
        </div>
      </div>
      <input
        ref="originalInput"
        class="premium-delivery-admin__input"
        type="file"
        multiple
        accept=".jpg,.jpeg,image/jpeg"
        @change="onOriginalInput"
        :disabled="uploading"
      />

      <ul v-if="deliveredOriginalImages.length" class="premium-delivery-admin__original-list">
        <li v-for="asset in deliveredOriginalImages" :key="asset.id" :title="asset.name">
          <img :src="originalPreviewUrl(asset)" :alt="asset.name || '전달된 원본 사진'" />
          <v-btn
            icon
            x-small
            color="#9d2d36"
            class="premium-delivery-admin__delete premium-delivery-admin__delete--original"
            :loading="deletingAssetId === asset.id"
            :disabled="uploading || deletingOriginals || Boolean(deletingAssetId)"
            :aria-label="`${asset.name || '원본 사진'} 삭제`"
            @click.stop="deleteOriginal(asset)"
          >
            <v-icon size="16">mdi-delete-outline</v-icon>
          </v-btn>
        </li>
      </ul>

      <div v-else-if="deliveredOriginal" class="premium-delivery-admin__original-file" :title="deliveredOriginal.name">
        <v-icon size="24" color="#7d1832">mdi-folder-zip-outline</v-icon>
        <span>
          <b>{{ deliveredOriginal.name || "원본 사진 모음.zip" }}</b>
          <small>{{ deliveredOriginalCount }}장 · {{ formatFileSize(deliveredOriginal.size || deliveredOriginal.size_bytes) }}</small>
        </span>
        <v-btn
          icon
          x-small
          color="#9d2d36"
          class="premium-delivery-admin__delete premium-delivery-admin__delete--original-file"
          :loading="deletingAssetId === deliveredOriginal.id"
          :disabled="uploading || deletingOriginals || Boolean(deletingAssetId)"
          :aria-label="`${deliveredOriginal.name || '원본 사진 모음'} 삭제`"
          @click.stop="deleteOriginal(deliveredOriginal)"
        >
          <v-icon size="17">mdi-delete-outline</v-icon>
        </v-btn>
      </div>

      <button
        v-else
        type="button"
        class="premium-delivery-admin__empty premium-delivery-admin__empty--originals"
        :disabled="uploading"
        @click="triggerOriginalInput"
      >
        원본 사진을 추가해 주세요
      </button>

      <div v-if="originals.length" class="premium-delivery-admin__pending-file">
        <span>
          <b>추가할 원본 사진 {{ originals.length }}장</b>
          <small>{{ formatFileSize(selectedOriginalBytes) }}</small>
        </span>
        <v-btn icon x-small :disabled="uploading" aria-label="선택한 원본 사진 모두 삭제" @click.stop="clearOriginalSelection">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>
    </section>

    <v-alert v-if="errorMessage" dense outlined type="error" class="mt-4 mb-0">
      {{ errorMessage }}
    </v-alert>

  </section>
</template>

<script>
import axios from "axios"

export default {
  name: "PremiumPhotoDeliveryUpload",
  props: {
    reservation: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    originals: [],
    completed: [],
    delivery: null,
    uploading: false,
    loadingDelivery: false,
    deliveryLoadError: false,
    deliveryRequestId: 0,
    draggingOriginals: false,
    draggingCompleted: false,
    errorMessage: "",
    uploadPercent: 0,
    uploadLoadedBytes: 0,
    uploadTotalBytes: 0,
    uploadFailed: false,
    uploadCancelSource: null,
    deletingAssetId: null,
    deletingOriginals: false,
  }),
  computed: {
    deliveredOriginals() {
      return this.delivery && Array.isArray(this.delivery.originals)
        ? this.delivery.originals
        : []
    },
    deliveredOriginal() {
      return this.deliveredOriginals[0] || null
    },
    deliveredOriginalImages() {
      return this.deliveredOriginals.filter((asset) => this.isImageAsset(asset))
    },
    deliveredOriginalCount() {
      if (this.deliveredOriginalImages.length) return this.deliveredOriginalImages.length
      return this.deliveredOriginals.reduce(
        (total, asset) => total + Math.max(0, Number(asset.item_count || 0)),
        0
      )
    },
    deliveredCompleted() {
      return this.delivery && Array.isArray(this.delivery.completed)
        ? this.delivery.completed
        : []
    },
    canPublish() {
      const hasSelectedFiles = this.originals.length > 0 || this.completed.length > 0
      return (
        hasSelectedFiles &&
        !this.uploading &&
        !this.deletingAssetId &&
        !this.deletingOriginals &&
        !this.loadingDelivery &&
        !this.deliveryLoadError
      )
    },
    selectedOriginalBytes() {
      return this.originals.reduce((sum, file) => sum + Number(file.size || 0), 0)
    },
    selectedCompletedBytes() {
      return this.completed.reduce((sum, file) => sum + Number(file.size || 0), 0)
    },
    selectedTotalBytes() {
      return this.selectedOriginalBytes + this.selectedCompletedBytes
    },
  },
  watch: {
    "reservation.id": {
      immediate: true,
      handler() {
        this.resetSelection()
        this.loadDelivery()
      },
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.preventPageExit)
    this.notifyState()
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.preventPageExit)
    this.cancelUpload(false)
  },
  methods: {
    isImageAsset(asset) {
      if (!asset) return false
      const contentType = String(asset.content_type || "").toLowerCase()
      const filename = String(asset.name || asset.filename || "").toLowerCase()
      return contentType.startsWith("image/") || /\.(jpe?g|png|webp)$/i.test(filename)
    },
    originalPreviewUrl(asset) {
      if (!asset) return ""
      if (asset.thumbnail_url && !asset.download_url) return asset.thumbnail_url
      if (!asset.id || !this.reservation || !this.reservation.id) {
        return asset.thumbnail_url || asset.url || ""
      }
      return `/api/admin/photo-deliveries/${this.reservation.id}/originals/${asset.id}/thumbnail`
    },
    async loadDelivery() {
      if (!this.reservation || !this.reservation.id) return
      const reservationId = this.reservation.id
      const requestId = ++this.deliveryRequestId
      this.loadingDelivery = true
      this.deliveryLoadError = false
      try {
        const { data } = await this.$http.get(
          `/api/admin/photo-deliveries/by-reservation/${reservationId}`
        )
        if (requestId !== this.deliveryRequestId || reservationId !== this.reservation.id) return
        this.delivery = data.delivery || null
      } catch (error) {
        if (requestId !== this.deliveryRequestId || reservationId !== this.reservation.id) return
        this.delivery = null
        this.deliveryLoadError = true
      } finally {
        if (requestId !== this.deliveryRequestId || reservationId !== this.reservation.id) return
        this.loadingDelivery = false
        this.notifyState()
      }
    },
    resetSelection() {
      this.originals = []
      this.completed = []
      this.errorMessage = ""
      this.resetUploadProgress()
      if (this.$refs.originalInput) this.$refs.originalInput.value = ""
      if (this.$refs.completedInput) this.$refs.completedInput.value = ""
      this.notifyState()
    },
    triggerOriginalInput() {
      if (!this.uploading && this.$refs.originalInput) this.$refs.originalInput.click()
    },
    triggerCompletedInput() {
      if (!this.uploading && this.$refs.completedInput) this.$refs.completedInput.click()
    },
    async onDropOriginals(event) {
      this.draggingOriginals = false
      if (this.uploading) return
      if (this.setOriginals(event.dataTransfer.files)) await this.publish()
    },
    async onDropCompleted(event) {
      this.draggingCompleted = false
      if (this.uploading) return
      if (this.setCompleted(event.dataTransfer.files)) await this.publish()
    },
    async onOriginalInput(event) {
      const accepted = this.setOriginals(event.target.files)
      event.target.value = ""
      if (accepted) await this.publish()
    },
    async onCompletedInput(event) {
      const accepted = this.setCompleted(event.target.files)
      event.target.value = ""
      if (accepted) await this.publish()
    },
    setOriginals(fileList) {
      const incoming = Array.from(fileList || []).filter((file) =>
        [".jpg", ".jpeg"].some((extension) => file.name.toLowerCase().endsWith(extension))
      )
      const known = new Set(this.originals.map(this.fileIdentity))
      const unique = incoming.filter((file) => {
        const identity = this.fileIdentity(file)
        if (known.has(identity)) return false
        known.add(identity)
        return true
      })
      this.originals = this.originals.concat(unique)
      this.errorMessage = incoming.length ? "" : "원본 사진은 JPG 파일만 등록할 수 있습니다."
      this.uploadFailed = false
      this.notifyState()
      return unique.length > 0
    },
    setCompleted(fileList) {
      const incoming = Array.from(fileList || []).filter((file) =>
        [".jpg", ".jpeg"].some((extension) => file.name.toLowerCase().endsWith(extension))
      )
      const known = new Set(this.completed.map(this.fileIdentity))
      const unique = incoming.filter((file) => {
        const identity = this.fileIdentity(file)
        if (known.has(identity)) return false
        known.add(identity)
        return true
      })
      this.completed = this.completed.concat(unique)
      this.errorMessage = incoming.length ? "" : "완성 사진은 JPG 파일만 등록할 수 있습니다."
      this.uploadFailed = false
      this.notifyState()
      return unique.length > 0
    },
    fileIdentity(file) {
      return [file.name, file.size, file.lastModified || 0].join("::")
    },
    removeOriginal(index) {
      if (this.uploading) return
      this.originals = this.originals.filter((file, fileIndex) => fileIndex !== index)
      if (this.$refs.originalInput) this.$refs.originalInput.value = ""
      this.notifyState()
    },
    clearOriginalSelection() {
      if (this.uploading) return
      this.originals = []
      if (this.$refs.originalInput) this.$refs.originalInput.value = ""
      this.notifyState()
    },
    removeCompleted(index) {
      if (this.uploading) return
      this.completed = this.completed.filter((file, fileIndex) => fileIndex !== index)
      if (this.$refs.completedInput) this.$refs.completedInput.value = ""
      this.notifyState()
    },
    clearSelection() {
      if (this.uploading) return
      this.resetSelection()
    },
    formatFileSize(size) {
      if (!Number.isFinite(size) || size <= 0) return "0 KB"
      if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    notifyState() {
      this.$nextTick(() => {
        this.$emit("state-change", {
          canPublish: this.canPublish,
          uploading: this.uploading,
          uploadPercent: this.uploadPercent,
        })
      })
    },
    resetUploadProgress() {
      this.uploadPercent = 0
      this.uploadLoadedBytes = 0
      this.uploadTotalBytes = 0
      this.uploadFailed = false
      this.uploadCancelSource = null
    },
    updateUploadProgress(event) {
      const total = Number(event && event.total) || this.selectedTotalBytes
      const loaded = Math.min(Number(event && event.loaded) || 0, total || Number.MAX_SAFE_INTEGER)
      this.uploadTotalBytes = total
      this.uploadLoadedBytes = loaded
      this.uploadPercent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0
      this.notifyState()
    },
    preventPageExit(event) {
      if (!this.uploading) return undefined
      event.preventDefault()
      event.returnValue = ""
      return ""
    },
    cancelUpload(showMessage = true) {
      if (this.uploadCancelSource) {
        this.uploadCancelSource.cancel("UPLOAD_CANCELLED")
        this.uploadCancelSource = null
      }
      if (showMessage && this.uploading) {
        this.errorMessage = "전송을 취소했습니다. 선택한 파일은 그대로 남아 있습니다."
      }
    },
    async publish() {
      if (!this.canPublish) {
        if (this.loadingDelivery) {
          this.errorMessage = "기존 전달 상태를 확인하고 있습니다. 잠시 후 다시 눌러 주세요."
        } else if (this.deliveryLoadError) {
          this.errorMessage = "기존 전달 상태를 확인하지 못해 전달할 수 없습니다. 예약 창을 다시 열어 주세요."
        } else if (!this.originals.length && !this.completed.length) {
          this.errorMessage = "추가할 원본 또는 완성 JPG 파일을 선택해 주세요."
        }
        return
      }
      const formData = new FormData()
      formData.append("reservation_id", this.reservation.id)
      this.originals.forEach((file) => formData.append("originals", file))
      this.completed.forEach((file) => formData.append("completed", file))
      this.uploading = true
      this.uploadFailed = false
      this.uploadPercent = 0
      this.uploadLoadedBytes = 0
      this.uploadTotalBytes = this.selectedTotalBytes
      this.uploadCancelSource = axios.CancelToken.source()
      this.errorMessage = ""
      this.notifyState()
      try {
        const { data } = await this.$http.post("/api/admin/photo-deliveries", formData, {
          cancelToken: this.uploadCancelSource.token,
          onUploadProgress: this.updateUploadProgress,
        })
        this.uploadPercent = 100
        this.uploadLoadedBytes = this.uploadTotalBytes
        this.delivery = data.delivery
        this.resetSelection()
        this.$emit("published", this.delivery)
        this.$toast.open({
          message: "고객 마이페이지에 사진을 전달했습니다.",
          type: "success",
        })
      } catch (error) {
        const cancelled = axios.isCancel(error)
        this.uploadFailed = true
        const code = error.response && error.response.data && error.response.data.error
        this.errorMessage = cancelled
          ? "전송을 취소했습니다. 선택한 파일은 그대로 남아 있습니다."
          :
          code === "UPLOAD_FILES_REQUIRED"
            ? "추가할 원본 또는 완성 JPG 파일을 선택해 주세요."
            : code === "INVALID_ORIGINAL_JPEG"
              ? "원본 사진은 정상적인 JPG 파일만 등록해 주세요."
              : code === "COMPLETED_JPEG_REQUIRED" || code === "INVALID_COMPLETED_JPEG"
              ? "완성 사진은 정상적인 JPG 파일만 등록해 주세요."
              : code === "ORIGINAL_TOO_LARGE"
                ? "원본 사진 용량이 너무 큽니다."
                : code === "COMPLETED_FILE_TOO_LARGE" || code === "COMPLETED_TOTAL_TOO_LARGE"
                  ? "완성 사진 용량이 너무 큽니다."
                  : code === "DELIVERY_PUBLISH_IN_PROGRESS"
                    ? "같은 예약의 사진을 전달하고 있습니다. 잠시 후 다시 시도해 주세요."
            : "사진을 전달하지 못했습니다. 파일을 확인한 뒤 다시 시도해 주세요."
      } finally {
        this.uploading = false
        this.uploadCancelSource = null
        this.notifyState()
      }
    },
    async deletePublished(asset) {
      if (!asset || !asset.id || this.uploading || this.deletingAssetId) return
      if (this.deliveredCompleted.length <= 1) {
        this.errorMessage = "마지막 완성사진은 삭제할 수 없습니다. 새 사진을 먼저 추가해 주세요."
        return
      }
      this.deletingAssetId = asset.id
      this.errorMessage = ""
      this.notifyState()
      try {
        const { data } = await this.$http.delete(
          `/api/admin/photo-deliveries/${this.reservation.id}/completed/${asset.id}`
        )
        this.delivery = data.delivery
        this.$emit("published", this.delivery)
        this.$toast.open({
          message: "완성사진을 삭제했습니다.",
          type: "success",
        })
      } catch (error) {
        const code = error.response && error.response.data && error.response.data.error
        this.errorMessage = code === "LAST_COMPLETED_PHOTO_REQUIRED"
          ? "마지막 완성사진은 삭제할 수 없습니다. 새 사진을 먼저 추가해 주세요."
          : "완성사진을 삭제하지 못했습니다. 다시 시도해 주세요."
      } finally {
        this.deletingAssetId = null
        this.notifyState()
      }
    },
    async deleteOriginal(asset, showSuccess = true) {
      if (
        !asset ||
        !asset.id ||
        this.uploading ||
        this.deletingOriginals ||
        this.deletingAssetId
      ) return false
      this.deletingAssetId = asset.id
      this.errorMessage = ""
      this.notifyState()
      try {
        const { data } = await this.$http.delete(
          `/api/admin/photo-deliveries/${this.reservation.id}/originals/${asset.id}`
        )
        this.delivery = data.delivery
        this.$emit("published", this.delivery)
        if (showSuccess) {
          this.$toast.open({
            message: "원본 사진을 삭제했습니다.",
            type: "success",
          })
        }
        return true
      } catch (_) {
        this.errorMessage = "원본 사진을 삭제하지 못했습니다. 다시 시도해 주세요."
        return false
      } finally {
        this.deletingAssetId = null
        this.notifyState()
      }
    },
    async deleteAllOriginals() {
      if (
        !this.deliveredOriginals.length ||
        this.uploading ||
        this.deletingOriginals ||
        this.deletingAssetId
      ) return
      this.deletingOriginals = true
      this.errorMessage = ""
      this.notifyState()
      try {
        for (const asset of [...this.deliveredOriginals]) {
          const { data } = await this.$http.delete(
            `/api/admin/photo-deliveries/${this.reservation.id}/originals/${asset.id}`
          )
          this.delivery = data.delivery
        }
        this.$emit("published", this.delivery)
        this.$toast.open({
          message: "원본 사진을 모두 삭제했습니다.",
          type: "success",
        })
      } catch (_) {
        this.errorMessage = "원본 사진 전체 삭제를 마치지 못했습니다. 화면을 다시 확인해 주세요."
        await this.loadDelivery()
      } finally {
        this.deletingOriginals = false
        this.notifyState()
      }
    },
  },
}
</script>

<style scoped>
.premium-delivery-admin {
  padding-top: 18px;
  color: #2d2926;
}

.premium-delivery-admin__status {
  display: flex;
  min-height: 38px;
  margin-bottom: 16px;
  color: #756c66;
  font-size: 13px;
  align-items: center;
  gap: 8px;
}

.premium-delivery-admin__status--complete {
  color: #98671d;
}

.premium-delivery-admin__shelf {
  min-width: 0;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e4dcd7;
  border-radius: 9px;
  background: #fbf9f7;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.premium-delivery-admin__shelf--active {
  border-color: #7d1832;
  background: #fffaf9;
  box-shadow: 0 0 0 3px rgba(125, 24, 50, 0.08);
}

.premium-delivery-admin__shelf-heading {
  display: flex;
  min-height: 34px;
  margin-bottom: 10px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.premium-delivery-admin__shelf-heading strong {
  color: #3c3531;
  font-size: 14px;
}

.premium-delivery-admin__shelf-heading small {
  margin-left: 4px;
  color: #786e68;
  font-size: 11px;
  font-weight: 600;
}

.premium-delivery-admin__heading-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.premium-delivery-admin__delete-all {
  min-width: 0 !important;
  height: 32px !important;
  padding: 0 8px !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.premium-delivery-admin__input {
  display: none;
}

.premium-delivery-admin__add {
  min-width: 0 !important;
  height: 34px !important;
  padding: 0 12px !important;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.premium-delivery-admin__completed-list,
.premium-delivery-admin__original-list {
  display: grid;
  width: 100%;
  margin: 0;
  padding: 0 4px 0 0;
  overflow-x: hidden;
  overflow-y: auto;
  list-style: none;
  align-content: start;
}

.premium-delivery-admin__completed-list {
  max-height: 236px;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 10px;
}

.premium-delivery-admin__original-list {
  max-height: 178px;
  grid-template-columns: repeat(auto-fill, minmax(58px, 1fr));
  gap: 6px;
}

.premium-delivery-admin__completed-list li,
.premium-delivery-admin__original-list li {
  position: relative;
  display: block;
  min-width: 0;
  overflow: hidden;
  border-radius: 7px;
  background: #f0ece9;
}

.premium-delivery-admin__completed-list img,
.premium-delivery-admin__original-list img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 5;
  background: #f0ece9;
  object-fit: cover;
}

.premium-delivery-admin__delete {
  position: absolute !important;
  top: 5px;
  right: 5px;
  width: 24px !important;
  height: 24px !important;
  background: rgba(255, 255, 255, 0.94) !important;
  box-shadow: 0 2px 8px rgba(42, 31, 27, 0.16);
}

.premium-delivery-admin__delete--original {
  top: 3px;
  right: 3px;
  width: 21px !important;
  height: 21px !important;
}

.premium-delivery-admin__empty {
  display: flex;
  width: 100%;
  min-height: 92px;
  border: 1px dashed #d9cfca;
  border-radius: 7px;
  background: #fff;
  color: #827771;
  font-size: 12px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.premium-delivery-admin__empty:hover,
.premium-delivery-admin__empty:focus-visible {
  border-color: #7d1832;
  color: #7d1832;
  outline: 0;
}

.premium-delivery-admin__empty--originals {
  min-height: 76px;
}

.premium-delivery-admin__original-file,
.premium-delivery-admin__pending-file {
  position: relative;
  display: flex;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #e5ddd8;
  border-radius: 7px;
  background: #fff;
  align-items: center;
  gap: 10px;
}

.premium-delivery-admin__original-file {
  padding-right: 42px;
}

.premium-delivery-admin__delete--original-file {
  top: 50%;
  right: 9px;
  width: 24px !important;
  height: 24px !important;
  transform: translateY(-50%);
}

.premium-delivery-admin__pending-file {
  margin-top: 8px;
}

.premium-delivery-admin__original-file span,
.premium-delivery-admin__pending-file span {
  display: flex;
  overflow: hidden;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.premium-delivery-admin__original-file b,
.premium-delivery-admin__pending-file b {
  overflow: hidden;
  color: #3c3531;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.premium-delivery-admin__original-file small,
.premium-delivery-admin__pending-file small {
  color: #817771;
  font-size: 10px;
}

.premium-delivery-admin__selection {
  display: flex;
  min-height: 54px;
  margin-top: 12px;
  padding: 9px 12px;
  border: 1px solid #e2dad5;
  border-radius: 8px;
  background: #fbf9f7;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.premium-delivery-admin__selection > div {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 14px;
}

.premium-delivery-admin__selection strong {
  color: #3c3531;
  font-size: 12px;
}

.premium-delivery-admin__selection span {
  color: #756b65;
  font-size: 11px;
}

.premium-delivery-admin__progress {
  position: relative;
  margin: 0 0 12px;
  padding: 14px 16px 13px;
  border: 1px solid #d9c9c1;
  border-radius: 9px;
  background: #fffaf8;
}

.premium-delivery-admin__progress-copy {
  display: flex;
  margin-bottom: 9px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.premium-delivery-admin__progress-copy strong {
  color: #4b3736;
  font-size: 13px;
}

.premium-delivery-admin__progress-copy span {
  color: #766a64;
  font-size: 11px;
}

.premium-delivery-admin__cancel {
  display: block;
  min-width: 0 !important;
  margin: 7px 0 -6px auto;
}

.premium-delivery-admin__published {
  color: #34745a;
}

@media (max-width: 720px) {
  .premium-delivery-admin__completed-list {
    grid-template-columns: repeat(2, minmax(92px, 1fr));
  }

  .premium-delivery-admin__original-list {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .premium-delivery-admin__shelf-heading {
    align-items: center;
  }

  .premium-delivery-admin__shelf--originals .premium-delivery-admin__shelf-heading {
    flex-wrap: wrap;
    gap: 6px;
  }

  .premium-delivery-admin__shelf--originals .premium-delivery-admin__shelf-heading > strong {
    flex: 1 0 100%;
    white-space: nowrap;
  }

  .premium-delivery-admin__heading-actions {
    flex: 0 0 auto;
    margin-left: auto;
  }

  .premium-delivery-admin__selection,
  .premium-delivery-admin__progress-copy {
    align-items: flex-start;
    flex-direction: column;
  }

  .premium-delivery-admin__add {
    padding: 0 9px !important;
  }
}
</style>
